/**
 * ==============================================
 * RARE CANVAS - CREATOR SERVICES FLOW
 * ==============================================
 *
 * HOW THE SERVICE PURCHASE PROCESS WORKS:
 *
 * 1. USER BROWSING
 *    - Creator visits /creator page
 *    - Clicks "Boost & Promote" or navigates to Services tab
 *    - Views available services with prices
 *
 * 2. SERVICE PURCHASE
 *    a) User clicks "Purchase Now" on desired service
 *    b) Review screen shows:
 *       - Service details & benefits
 *       - Price in KLV
 *       - Required materials (for manual services)
 *    c) User clicks "Pay X KLV" button
 *    d) Transaction is built:
 *       - Type 0 (Transfer) to PLATFORM_FEE_WALLET
 *       - Amount: service price × 10^6 (KLV precision)
 *       - Data field: JSON with service ID & target collection
 *    e) User signs in Klever Wallet
 *    f) Transaction broadcasts to blockchain
 *
 * 3. PURCHASE TRACKING
 *    - Transaction hash is captured from response
 *    - Purchase data saved to localStorage:
 *      key: 'rarecanvas_service_purchases'
 *      data: { service, wallet, targetAsset, txHash, amount, timestamp, status }
 *    - User sees success message with tx link to Kleverscan
 *
 * 4. ADMIN PROCESSING (at /admin)
 *    a) Admin connects with authorized wallet
 *    b) Views pending purchases in "Service Purchases" tab
 *    c) Can also check "Blockchain Txs" tab to verify payments
 *    d) For each purchase:
 *       - Verifies transaction on Kleverscan
 *       - For automatic services: approves immediately
 *       - For manual services: waits for material submission
 *    e) Clicks "Approve" to activate service
 *
 * 5. WHAT HAPPENS ON APPROVAL
 *    - Featured ITO: Added to 'rarecanvas_featured_collections'
 *      → Appears in FeaturedITOs component on /mint page
 *      → Expires after 7 days
 *    - Verified Creator: Added to 'rarecanvas_verified_creators'
 *      → Badge shows on collections
 *    - Launch Boost: Added to featured with 14-day duration
 *      → Premium placement + badge on /mint page
 *    - Premium Analytics: (Future feature)
 *
 * 6. MANUAL SERVICES WORKFLOW
 *    a) After payment, user sees submission instructions
 *    b) User sends materials to Telegram/Twitter:
 *       - Transaction hash (proof of payment)
 *       - Wallet address
 *       - Required files (banners, logos, etc.)
 *    c) Admin reviews materials
 *    d) If approved: clicks "Approve" in admin panel
 *    e) If rejected: clicks "Reject" with reason
 *
 * STORAGE KEYS:
 *    - rarecanvas_service_purchases: All purchase records
 *    - rarecanvas_featured_collections: Active featured ITOs
 *    - rarecanvas_verified_creators: Verified creator wallets
 *
 * CONTACT INFO:
 *    - Telegram: @RareCanvasSupport
 *    - Twitter: @RareCanvasNFT
 * ==============================================
 */

import React, { useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useWallet } from '../components/WalletContext';
import { web } from '@klever/sdk-web';
import { useNavigate } from 'react-router-dom';
import {
  FaPalette,
  FaWallet,
  FaPlus,
  FaRocket,
  FaChartLine,
  FaLayerGroup,
  FaBookOpen,
  FaArrowRight,
  FaCheckCircle,
  FaExternalLinkAlt,
  FaImage,
  FaCoins,
  FaPercent,
  FaInfoCircle,
  FaLink,
  FaCubes,
  FaFire,
  FaCog,
  FaStar,
  FaCrown,
  FaBolt,
  FaGem,
  FaShieldAlt,
  FaBullhorn,
  FaSpinner,
  FaTimes,
  FaCheck,
  FaCopy,
  FaUpload
} from 'react-icons/fa';
import CreateCollectionModal from '../components/CreateCollectionModal';
import ConfigITOModal from '../components/ConfigITOModal';
import ManageCollectionModal from '../components/ManageCollectionModal';
import CreatorGuide from '../components/CreatorGuide';
import '../styles/CreatorPage.css';

// Platform fee wallet address - receives all service payments
// This is the admin wallet that receives fees
const PLATFORM_FEE_WALLET = 'klv1pwh5qeqh5s582s93sx0d3tfsnjth85jevugtgclwtg7sjzpp9muqw877kl';

// Service pricing (in KLV)
const SERVICES = {
  DROPS_LISTING: {
    id: 'drops_listing',
    name: 'Drops Page Listing',
    description: 'Get your upcoming drop featured on the exclusive Drops page',
    price: 5000,
    duration: '30 days',
    icon: FaRocket,
    benefits: [
      'Premium Drops page placement',
      'Countdown timer display',
      'Full project showcase',
      'Social links & description',
      'Email notification to subscribers',
      'Twitter & Telegram promotion'
    ],
    requirements: [
      'Project Banner: 1920x600px (PNG/JPG, max 1MB)',
      'Project Logo: 400x400px (PNG, transparent bg)',
      'Full Description: Project story, utility, roadmap',
      'Drop Date & Time (UTC)',
      'Mint Price & Supply info',
      'Twitter & Telegram links',
      'Website URL (optional)'
    ],
    processing: 'manual_review'
  },
  LAUNCH_BOOST: {
    id: 'launch_boost',
    name: 'Launch Boost',
    description: 'Premium launch package with marketing and visibility boost',
    price: 2500,
    duration: '14 days',
    icon: FaBullhorn,
    benefits: ['Homepage banner (1400x400px)', 'Twitter promotion post', 'Telegram channel announcement', 'Priority mint page listing'],
    requirements: [
      'Homepage Banner: 1400x400px (PNG/JPG, max 500KB)',
      'Project Logo: 400x400px (PNG, transparent bg preferred)',
      'Short Description: Max 280 characters',
      'Twitter/Telegram links for cross-promotion'
    ],
    processing: 'manual_review'
  },
  VERIFIED_CREATOR: {
    id: 'verified_creator',
    name: 'Verified Creator',
    description: 'Get a verified badge for your creator profile and collections',
    price: 1000,
    duration: 'Lifetime',
    icon: FaShieldAlt,
    benefits: ['Verified checkmark badge', 'Priority in search results', 'Increased buyer trust', 'Telegram verified role'],
    requirements: [
      'Active collection with at least 10 mints',
      'Valid social media presence (Twitter/Telegram)',
      'No history of scams or rug pulls'
    ],
    processing: 'manual_review'
  },
  FEATURED_ITO: {
    id: 'featured_ito',
    name: 'Featured ITO',
    description: 'Get your ITO featured at the top of the Mint page for 7 days',
    price: 500,
    duration: '7 days',
    icon: FaStar,
    benefits: ['Top placement on Mint page', 'Gold border highlight', 'Featured badge', '3x more visibility'],
    requirements: null, // Auto-processed from collection data
    processing: 'automatic'
  }
};

// Contact info for service submissions
const CONTACT_INFO = {
  telegram: '@RareCanvasSupport', // Update with your actual Telegram
  twitter: '@RareCanvasNFT',      // Update with your actual Twitter
  email: 'creators@rarecanvas.io' // Optional email
};

// Helper to check if ITO is truly active (not sold out and not ended)
const isItoTrulyActive = (ito) => {
  if (!ito.isActive) return false;
  if (ito.maxAmount > 0 && ito.mintedAmount >= ito.maxAmount) return false;
  if (ito.endTime > 0 && ito.endTime < Math.floor(Date.now() / 1000)) return false;
  return true;
};

// Fetch function for user collections
const fetchUserCollections = async (walletAddress) => {
  if (!walletAddress) return { collections: [], itos: [], stats: { totalCollections: 0, activeItos: 0, totalMinted: 0, totalVolume: 0 } };

  // Use the assets/list endpoint with owner filter - much simpler!
  const assetsResponse = await axios.get(
    `https://api.mainnet.klever.org/v1.0/assets/list?owner=${walletAddress}&limit=100`
  );

  const allAssets = assetsResponse.data.data?.assets || [];

  // Filter to only NFT/SFT collections
  const collections = allAssets.filter(
    asset => asset.assetType === 'NonFungible' || asset.assetType === 'SemiFungible'
  );

  // Fetch ITOs for each collection
  const itos = [];
  for (const collection of collections) {
    try {
      const itoResponse = await axios.get(
        `https://api.mainnet.klever.org/v1.0/ito/${collection.assetId}`
      );
      const ito = itoResponse.data.data?.ito;
      if (ito) {
        itos.push(ito);
      }
    } catch (err) {
      // ITO doesn't exist for this collection - that's fine
    }
  }

  // Calculate stats
  const activeCount = itos.filter(ito => isItoTrulyActive(ito)).length;
  const totalMinted = itos.reduce((sum, ito) => sum + (ito.mintedAmount || 0), 0);
  const totalVolume = itos.reduce((sum, ito) => {
    const klvFunds = ito.fundsRaised?.find(f => f.assetId === 'KLV');
    return sum + (klvFunds?.amount || 0);
  }, 0) / Math.pow(10, 6);

  return {
    collections,
    itos,
    stats: {
      totalCollections: collections.length,
      activeItos: activeCount,
      totalMinted,
      totalVolume
    }
  };
};

const CreatorPage = () => {
  const { isConnected, walletAddress, connectWallet } = useWallet();
  const navigate = useNavigate();

  // UI State
  const [activeTab, setActiveTab] = useState('collections');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showITOModal, setShowITOModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);

  // Services state
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceTarget, setServiceTarget] = useState(null); // Collection/ITO for service
  const [purchaseState, setPurchaseState] = useState(''); // 'processing', 'success', 'error'
  const [purchaseMessage, setPurchaseMessage] = useState('');

  // Use React Query for data fetching with built-in caching
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['creatorData', walletAddress],
    queryFn: () => fetchUserCollections(walletAddress),
    enabled: isConnected && !!walletAddress,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });

  const userCollections = data?.collections || [];
  const userItos = data?.itos || [];
  const stats = data?.stats || { totalCollections: 0, activeItos: 0, totalMinted: 0, totalVolume: 0 };

  // Handle collection click to view details
  const handleCollectionClick = (collection) => {
    navigate(`/collections/${collection.assetId}`);
  };

  // Handle configure ITO for a collection
  const handleConfigureITO = (collection) => {
    setSelectedCollection(collection);
    setShowITOModal(true);
  };

  // Handle manage collection
  const handleManageCollection = (collection) => {
    setSelectedCollection(collection);
    setShowManageModal(true);
  };

  // State for transaction hash display
  const [purchaseTxHash, setPurchaseTxHash] = useState('');
  const [hashCopied, setHashCopied] = useState(false);

  // Copy transaction hash to clipboard
  const copyTxHash = () => {
    if (purchaseTxHash) {
      navigator.clipboard.writeText(purchaseTxHash);
      setHashCopied(true);
      setTimeout(() => setHashCopied(false), 2000);
    }
  };

  // Handle service purchase
  const handlePurchaseService = async (service, targetAsset = null) => {
    if (!isConnected) {
      connectWallet();
      return;
    }

    setPurchaseState('processing');
    setPurchaseMessage('Initializing wallet...');
    setPurchaseTxHash('');
    setHashCopied(false);

    try {
      // IMPORTANT: Always initialize the SDK before transactions
      await web.initialize();

      // Verify we have a wallet address
      const address = await web.getWalletAddress();
      if (!address) {
        throw new Error('Wallet not connected. Please reconnect your wallet.');
      }

      console.log('Wallet initialized:', address);
      setPurchaseMessage('Building transaction...');

      // Build transfer transaction to platform wallet
      // Using Transfer contract (Type 0) with proper KLV format
      const amountInKLV = service.price * 1000000; // KLV has 6 decimals

      const transferContract = {
        type: 0, // Transfer
        payload: {
          amount: amountInKLV,
          receiver: PLATFORM_FEE_WALLET,
          kda: 'KLV'
        }
      };

      console.log('Building transaction with contract:', transferContract);
      console.log('Receiver address:', PLATFORM_FEE_WALLET);

      // Use same pattern as AuctionListingModal - pass empty array as second param
      const unsignedTx = await web.buildTransaction([transferContract], []);
      console.log('Unsigned transaction:', unsignedTx);

      if (!unsignedTx) {
        throw new Error('Failed to build transaction. Please try again.');
      }

      setPurchaseMessage('Please sign the transaction in your Klever wallet...');

      const signedTx = await web.signTransaction(unsignedTx);
      console.log('Signed transaction:', signedTx);

      if (!signedTx) {
        throw new Error('Transaction signing was cancelled.');
      }

      setPurchaseMessage('Broadcasting transaction...');
      const response = await web.broadcastTransactions([signedTx]);

      console.log('Broadcast response:', response);

      // Extract transaction hash - handle all possible response formats from Klever SDK
      let txHash = null;

      if (response?.data?.txsHashes?.[0]) {
        txHash = response.data.txsHashes[0];
      } else if (response?.txsHashes?.[0]) {
        txHash = response.txsHashes[0];
      } else if (Array.isArray(response) && response[0]?.hash) {
        txHash = response[0].hash;
      } else if (typeof response === 'object' && response.hash) {
        txHash = response.hash;
      }

      if (!txHash) {
        console.error('Could not extract tx hash from response:', response);
        throw new Error('Transaction may have been submitted but no hash was returned. Please check Kleverscan.');
      }

      setPurchaseTxHash(txHash);
      console.log('Transaction hash:', txHash);

      // Store purchase in localStorage for admin panel tracking
      const purchaseData = {
        service: service.id,
        serviceName: service.name,
        wallet: walletAddress,
        targetAsset: targetAsset?.assetId || null,
        targetName: targetAsset?.name || null,
        txHash: txHash,
        amount: service.price,
        timestamp: Date.now(),
        status: 'pending', // pending, approved, rejected
        processing: service.processing
      };

      // Save to localStorage (admin panel will read this)
      const existingPurchases = JSON.parse(localStorage.getItem('rarecanvas_service_purchases') || '[]');
      existingPurchases.push(purchaseData);
      localStorage.setItem('rarecanvas_service_purchases', JSON.stringify(existingPurchases));

      console.log('Purchase saved:', purchaseData);

      setPurchaseState('success');
      setPurchaseMessage(
        service.processing === 'automatic'
          ? `Payment successful! Your ${service.name} will be activated shortly.`
          : `Payment successful! Copy your transaction hash below and submit your materials.`
      );

      // Don't auto-close - user needs to copy the hash

    } catch (error) {
      console.error('Service purchase error:', error);
      setPurchaseState('error');

      // Better error messages
      let errorMsg = 'Transaction failed. Please try again.';
      if (error.message?.includes('rejected') || error.message?.includes('cancelled') || error.message?.includes('denied')) {
        errorMsg = 'Transaction was cancelled.';
      } else if (error.message?.includes('insufficient') || error.message?.includes('balance')) {
        errorMsg = `Insufficient KLV balance. You need ${service.price} KLV.`;
      } else if (error.message?.includes('not connected') || error.message?.includes('No wallet')) {
        errorMsg = 'Wallet not connected. Please refresh and reconnect.';
      } else if (error.message) {
        errorMsg = error.message;
      }

      setPurchaseMessage(errorMsg);
    }
  };

  // Open services modal for specific collection/ITO
  const handleOpenServices = (collection = null) => {
    setServiceTarget(collection);
    setShowServicesModal(true);
  };

  // Render loading state
  if (!isConnected) {
    return (
      <div className="creator-page">
        <div className="creator-connect-prompt">
          <div className="connect-content">
            <FaWallet className="connect-icon" />
            <h2>Connect Your Wallet</h2>
            <p>Connect your wallet to access the Creator Dashboard and start creating collections.</p>
            <button className="connect-button" onClick={connectWallet}>
              <FaWallet /> Connect Wallet
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="creator-page">
      {/* Hero Section */}
      <div className="creator-hero">
        <div className="creator-hero-content">
          <h1 className="creator-hero-title">
            <FaPalette className="hero-icon" />
            Creator Dashboard
          </h1>
          <p className="creator-hero-subtitle">
            Create, manage, and launch your NFT collections on KleverChain
          </p>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="creator-stats-bar">
        <div className="stat-item">
          <FaLayerGroup className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">Collections</span>
            <span className="stat-value">{stats.totalCollections}</span>
          </div>
        </div>
        <div className="stat-item">
          <FaRocket className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">Active ITOs</span>
            <span className="stat-value">{stats.activeItos}</span>
          </div>
        </div>
        <div className="stat-item">
          <FaCubes className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">Total Minted</span>
            <span className="stat-value">{stats.totalMinted.toLocaleString()}</span>
          </div>
        </div>
        <div className="stat-item">
          <FaChartLine className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">Volume</span>
            <span className="stat-value">{stats.totalVolume.toLocaleString()} KLV</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="creator-quick-actions">
        <button
          className="quick-action-btn primary"
          onClick={() => setShowCreateModal(true)}
        >
          <FaPlus /> Create Collection
        </button>
        <button
          className="quick-action-btn premium"
          onClick={() => setActiveTab('services')}
        >
          <FaCrown /> Boost & Promote
        </button>
        <button
          className="quick-action-btn secondary"
          onClick={() => setActiveTab('guide')}
        >
          <FaBookOpen /> View Guide
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="creator-tabs">
        <button
          className={`tab-btn ${activeTab === 'collections' ? 'active' : ''}`}
          onClick={() => setActiveTab('collections')}
        >
          <FaLayerGroup /> My Collections
        </button>
        <button
          className={`tab-btn ${activeTab === 'itos' ? 'active' : ''}`}
          onClick={() => setActiveTab('itos')}
        >
          <FaRocket /> Active ITOs
        </button>
        <button
          className={`tab-btn premium ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => setActiveTab('services')}
        >
          <FaCrown /> Services
        </button>
        <button
          className={`tab-btn ${activeTab === 'guide' ? 'active' : ''}`}
          onClick={() => setActiveTab('guide')}
        >
          <FaBookOpen /> Creator Guide
        </button>
      </div>

      {/* Tab Content */}
      <div className="creator-content">
        {/* Collections Tab */}
        {activeTab === 'collections' && (
          <div className="collections-section">
            {isLoading ? (
              <div className="creator-loading">
                <div className="loading-spinner-large"></div>
                <p>Loading your collections...</p>
              </div>
            ) : userCollections.length > 0 ? (
              <div className="collections-grid">
                {userCollections.map((collection) => {
                  const ito = userItos.find(ito => ito.assetId === collection.assetId);
                  const hasIto = !!ito;
                  const itoIsActive = ito ? isItoTrulyActive(ito) : false;
                  const klvRaised = ito?.fundsRaised?.find(f => f.assetId === 'KLV');
                  const fundsRaisedKLV = (klvRaised?.amount || 0) / Math.pow(10, 6);

                  return (
                    <div
                      key={collection.assetId}
                      className="collection-card"
                      onClick={() => handleCollectionClick(collection)}
                    >
                      <div className="collection-card-header">
                        {collection.logo ? (
                          <img
                            src={collection.logo.replace('ipfs://', 'https://ipfs.io/ipfs/')}
                            alt={collection.name}
                            className="collection-image"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className="collection-placeholder" style={{ display: collection.logo ? 'none' : 'flex' }}>
                          <FaImage size={32} />
                        </div>
                        <div className="collection-type-badge">
                          {collection.assetType === 'NonFungible' ? 'NFT' : 'SFT'}
                        </div>
                        {hasIto && (
                          <div className={`collection-ito-badge ${itoIsActive ? 'active' : 'ended'}`}>
                            {itoIsActive ? <><FaFire /> Live</> : 'Ended'}
                          </div>
                        )}
                      </div>
                      <div className="collection-card-body">
                        <h3 className="collection-name">{collection.name || 'Unnamed Collection'}</h3>
                        <p className="collection-ticker">{collection.assetId}</p>
                        <div className="collection-stats">
                          <div className="collection-stat">
                            <span className="stat-label">Supply</span>
                            <span className="stat-value">{collection.circulatingSupply?.toLocaleString() || 0}</span>
                          </div>
                          <div className="collection-stat">
                            <span className="stat-label">Max</span>
                            <span className="stat-value">{collection.maxSupply?.toLocaleString() || '∞'}</span>
                          </div>
                          {hasIto && fundsRaisedKLV > 0 && (
                            <div className="collection-stat raised">
                              <span className="stat-label">Raised</span>
                              <span className="stat-value">{fundsRaisedKLV.toLocaleString()} KLV</span>
                            </div>
                          )}
                        </div>
                        <div className="collection-actions">
                          <button
                            className="collection-action-btn manage"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleManageCollection(collection);
                            }}
                          >
                            <FaCog /> Manage
                          </button>
                          <button
                            className={`collection-action-btn ${hasIto ? 'edit' : 'create'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleConfigureITO(collection);
                            }}
                          >
                            <FaRocket /> {hasIto ? 'ITO' : 'Create ITO'}
                          </button>
                          {hasIto && itoIsActive && (
                            <button
                              className="collection-action-btn promote"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenServices(collection);
                              }}
                            >
                              <FaStar /> Promote
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Add Collection Card */}
                <div
                  className="collection-card add-card"
                  onClick={() => setShowCreateModal(true)}
                >
                  <div className="add-card-content">
                    <FaPlus className="add-icon" />
                    <span>Create New Collection</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <FaLayerGroup className="empty-icon" />
                <h3>No Collections Yet</h3>
                <p>You haven't created any collections. Start your creator journey now!</p>
                <button
                  className="empty-action-btn"
                  onClick={() => setShowCreateModal(true)}
                >
                  <FaPlus /> Create Your First Collection
                </button>
              </div>
            )}
          </div>
        )}

        {/* ITOs Tab - Show only truly active ITOs */}
        {activeTab === 'itos' && (
          <div className="itos-section">
            {userItos.filter(ito => isItoTrulyActive(ito)).length > 0 ? (
              <div className="itos-grid">
                {userItos.filter(ito => isItoTrulyActive(ito)).map((ito) => {
                  const collection = userCollections.find(c => c.assetId === ito.assetId);
                  const progress = ito.maxAmount > 0 ? (ito.mintedAmount / ito.maxAmount) * 100 : 0;
                  const klvRaised = ito.fundsRaised?.find(f => f.assetId === 'KLV');
                  const fundsRaisedKLV = (klvRaised?.amount || 0) / Math.pow(10, 6);

                  return (
                    <div key={ito.assetId} className="ito-card">
                      <div className="ito-card-header">
                        <div className="ito-image-wrapper">
                          {collection?.logo ? (
                            <img
                              src={collection.logo.replace('ipfs://', 'https://ipfs.io/ipfs/')}
                              alt={collection.name}
                              className="ito-image"
                            />
                          ) : (
                            <div className="ito-placeholder">
                              <FaRocket size={24} />
                            </div>
                          )}
                        </div>
                        <div className="ito-status active">
                          <FaFire /> Live
                        </div>
                      </div>
                      <div className="ito-card-body">
                        <h3 className="ito-name">{collection?.name || ito.assetId}</h3>
                        <p className="ito-asset-id">{ito.assetId}</p>
                        <div className="ito-progress">
                          <div className="progress-labels">
                            <span>Progress</span>
                            <span>{ito.mintedAmount?.toLocaleString()} / {ito.maxAmount?.toLocaleString()}</span>
                          </div>
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="ito-details">
                          <div className="ito-detail">
                            <FaCoins className="detail-icon" />
                            <span>
                              {ito.packData?.[0]?.packs?.[0]?.price / Math.pow(10, 6) || 0} {ito.packData?.[0]?.key || 'KLV'}
                            </span>
                          </div>
                          <div className="ito-detail funds-raised">
                            <FaChartLine className="detail-icon" />
                            <span>{fundsRaisedKLV.toLocaleString()} KLV raised</span>
                          </div>
                        </div>
                        <button
                          className="ito-manage-btn"
                          onClick={() => handleConfigureITO(collection)}
                        >
                          Manage ITO <FaArrowRight />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <FaRocket className="empty-icon" />
                <h3>No Active ITOs</h3>
                <p>You don't have any minting campaigns yet. Configure an ITO for your collection to start selling!</p>
                {userCollections.length > 0 ? (
                  <button
                    className="empty-action-btn"
                    onClick={() => handleConfigureITO(userCollections[0])}
                  >
                    <FaRocket /> Configure ITO
                  </button>
                ) : (
                  <button
                    className="empty-action-btn"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <FaPlus /> Create Collection First
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Guide Tab */}
        {activeTab === 'guide' && (
          <CreatorGuide />
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="services-section">
            <div className="services-header">
              <h2><FaCrown /> Creator Services</h2>
              <p>Boost your collections with our premium services. Increase visibility, build trust, and grow your audience.</p>
            </div>

            <div className="services-grid">
              {Object.values(SERVICES).map((service) => {
                const Icon = service.icon;
                return (
                  <div key={service.id} className={`service-card ${service.id}`}>
                    <div className="service-card-header">
                      <div className="service-icon">
                        <Icon />
                      </div>
                      <div className="service-badges">
                        <div className="service-badge">{service.duration}</div>
                        <div className={`service-badge processing ${service.processing}`}>
                          {service.processing === 'automatic' ? 'Auto' : 'Review'}
                        </div>
                      </div>
                    </div>
                    <div className="service-card-body">
                      <h3 className="service-name">{service.name}</h3>
                      <p className="service-description">{service.description}</p>
                      <div className="service-price">
                        <FaCoins />
                        <span className="price-amount">{service.price.toLocaleString()}</span>
                        <span className="price-currency">KLV</span>
                      </div>
                      <ul className="service-benefits">
                        {service.benefits.map((benefit, idx) => (
                          <li key={idx}>
                            <FaCheck className="benefit-check" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                      {service.requirements && (
                        <div className="service-requirements-note">
                          <FaInfoCircle /> Requires material submission
                        </div>
                      )}
                      <button
                        className="service-buy-btn"
                        onClick={() => {
                          setSelectedService(service);
                          setShowServicesModal(true);
                        }}
                      >
                        <FaBolt /> Purchase Now
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Why Use Our Services */}
            <div className="services-why">
              <h3><FaGem /> Why Use Our Services?</h3>
              <div className="why-grid">
                <div className="why-item">
                  <FaChartLine className="why-icon" />
                  <h4>Increase Sales</h4>
                  <p>Featured collections see up to 5x more mints on average</p>
                </div>
                <div className="why-item">
                  <FaShieldAlt className="why-icon" />
                  <h4>Build Trust</h4>
                  <p>Verified creators have 70% higher conversion rates</p>
                </div>
                <div className="why-item">
                  <FaBullhorn className="why-icon" />
                  <h4>Reach More Buyers</h4>
                  <p>Our marketing reaches 50,000+ active NFT collectors</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Collection Modal */}
      {showCreateModal && (
        <CreateCollectionModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            // Trigger re-fetch of collections
            refetch();
          }}
        />
      )}

      {/* Configure ITO Modal */}
      {showITOModal && selectedCollection && (
        <ConfigITOModal
          collection={selectedCollection}
          existingIto={userItos.find(ito => ito.assetId === selectedCollection.assetId)}
          onClose={() => {
            setShowITOModal(false);
            setSelectedCollection(null);
          }}
          onSuccess={() => {
            setShowITOModal(false);
            setSelectedCollection(null);
            // Trigger re-fetch of data
            refetch();
          }}
        />
      )}

      {/* Manage Collection Modal */}
      {showManageModal && selectedCollection && (
        <ManageCollectionModal
          collection={selectedCollection}
          onClose={() => {
            setShowManageModal(false);
            setSelectedCollection(null);
          }}
          onSuccess={() => {
            setShowManageModal(false);
            setSelectedCollection(null);
            // Trigger re-fetch of data
            refetch();
          }}
        />
      )}

      {/* Services Purchase Modal */}
      {showServicesModal && (
        <div className="services-modal-overlay" onClick={() => {
          if (purchaseState !== 'processing') {
            setShowServicesModal(false);
            setSelectedService(null);
            setServiceTarget(null);
            setPurchaseState('');
          }
        }}>
          <div className="services-modal" onClick={(e) => e.stopPropagation()}>
            <div className="services-modal-header">
              <h2>
                {selectedService ? (
                  <><selectedService.icon /> {selectedService.name}</>
                ) : (
                  <><FaCrown /> Select a Service</>
                )}
              </h2>
              <button
                className="services-modal-close"
                onClick={() => {
                  if (purchaseState !== 'processing') {
                    setShowServicesModal(false);
                    setSelectedService(null);
                    setServiceTarget(null);
                    setPurchaseState('');
                  }
                }}
                disabled={purchaseState === 'processing'}
              >
                <FaTimes />
              </button>
            </div>

            <div className="services-modal-body">
              {!selectedService ? (
                // Service selection view
                <div className="service-selection">
                  {serviceTarget && (
                    <div className="service-target-info">
                      <FaLayerGroup />
                      <span>For: <strong>{serviceTarget.name || serviceTarget.assetId}</strong></span>
                    </div>
                  )}
                  <p>Select a service to boost your {serviceTarget ? 'collection' : 'creator profile'}:</p>
                  <div className="service-options">
                    {Object.values(SERVICES).map((service) => {
                      const Icon = service.icon;
                      // Filter services based on whether a collection is selected
                      if (serviceTarget && service.id === 'verified_creator') return null;
                      if (!serviceTarget && (service.id === 'featured_ito' || service.id === 'premium_analytics')) return null;

                      return (
                        <button
                          key={service.id}
                          className={`service-option ${service.id}`}
                          onClick={() => setSelectedService(service)}
                        >
                          <Icon className="service-option-icon" />
                          <div className="service-option-info">
                            <span className="service-option-name">{service.name}</span>
                            <span className="service-option-price">{service.price.toLocaleString()} KLV</span>
                          </div>
                          <FaArrowRight className="service-option-arrow" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                // Purchase confirmation view
                <div className="service-purchase">
                  {serviceTarget && (
                    <div className="service-target-info">
                      <FaLayerGroup />
                      <span>For: <strong>{serviceTarget.name || serviceTarget.assetId}</strong></span>
                    </div>
                  )}

                  <div className="purchase-summary">
                    <div className="purchase-service-icon">
                      <selectedService.icon />
                    </div>
                    <h3>{selectedService.name}</h3>
                    <p>{selectedService.description}</p>

                    <div className="purchase-details">
                      <div className="purchase-detail">
                        <span className="detail-label">Duration</span>
                        <span className="detail-value">{selectedService.duration}</span>
                      </div>
                      <div className="purchase-detail price">
                        <span className="detail-label">Price</span>
                        <span className="detail-value">
                          <FaCoins /> {selectedService.price.toLocaleString()} KLV
                        </span>
                      </div>
                      <div className="purchase-detail">
                        <span className="detail-label">Processing</span>
                        <span className="detail-value">
                          {selectedService.processing === 'automatic' ? 'Automatic' : 'Manual Review'}
                        </span>
                      </div>
                    </div>

                    <ul className="purchase-benefits">
                      {selectedService.benefits.map((benefit, idx) => (
                        <li key={idx}>
                          <FaCheckCircle /> {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Requirements Section - for services that need submissions */}
                  {selectedService.requirements && (
                    <div className="purchase-requirements">
                      <h4><FaInfoCircle /> Required Materials</h4>
                      <p className="requirements-intro">
                        After payment, send these materials to our team:
                      </p>
                      <ul className="requirements-list">
                        {selectedService.requirements.map((req, idx) => (
                          <li key={idx}>{req}</li>
                        ))}
                      </ul>
                      <div className="submission-contacts">
                        <p><strong>Submit materials to:</strong></p>
                        <div className="contact-options">
                          <a
                            href={`https://t.me/${CONTACT_INFO.telegram.replace('@', '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="contact-link telegram"
                          >
                            <FaArrowRight /> Telegram: {CONTACT_INFO.telegram}
                          </a>
                          <a
                            href={`https://twitter.com/${CONTACT_INFO.twitter.replace('@', '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="contact-link twitter"
                          >
                            <FaArrowRight /> Twitter: {CONTACT_INFO.twitter}
                          </a>
                        </div>
                        <p className="submission-note">
                          Include your <strong>transaction hash</strong> and <strong>wallet address</strong> when submitting.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Auto-processing notice */}
                  {selectedService.processing === 'automatic' && (
                    <div className="auto-processing-notice">
                      <FaCheckCircle />
                      <span>This service is activated automatically after payment confirmation.</span>
                    </div>
                  )}

                  {/* Transaction Status */}
                  {purchaseState && (
                    <div className={`purchase-status ${purchaseState}`}>
                      {purchaseState === 'processing' && <FaSpinner className="spin" />}
                      {purchaseState === 'success' && <FaCheckCircle />}
                      {purchaseState === 'error' && <FaTimes />}
                      <span>{purchaseMessage}</span>
                    </div>
                  )}

                  {/* Transaction Hash Box - Important for users to copy */}
                  {purchaseState === 'success' && purchaseTxHash && (
                    <div className="tx-hash-box">
                      <div className="tx-hash-label">Transaction Hash (save this!):</div>
                      <div className="tx-hash-content">
                        <code className="tx-hash-code">{purchaseTxHash}</code>
                        <button className="tx-copy-btn" onClick={copyTxHash}>
                          {hashCopied ? <><FaCheck /> Copied!</> : <><FaCopy /> Copy</>}
                        </button>
                      </div>
                      <div className="tx-hash-actions">
                        <a
                          href={`https://kleverscan.org/transaction/${purchaseTxHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tx-view-link"
                        >
                          <FaExternalLinkAlt /> View on Kleverscan
                        </a>
                        {selectedService.processing !== 'automatic' && (
                          <a href="/submit-materials" className="tx-submit-link">
                            <FaUpload /> Submit Materials
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="purchase-actions">
                    {purchaseState === 'success' ? (
                      <button
                        className="purchase-done-btn"
                        onClick={() => {
                          setShowServicesModal(false);
                          setSelectedService(null);
                          setServiceTarget(null);
                          setPurchaseState('');
                          setPurchaseMessage('');
                          setPurchaseTxHash('');
                          setHashCopied(false);
                        }}
                      >
                        <FaCheck /> Done
                      </button>
                    ) : (
                      <>
                        <button
                          className="purchase-back-btn"
                          onClick={() => setSelectedService(null)}
                          disabled={purchaseState === 'processing'}
                        >
                          Back
                        </button>
                        <button
                          className="purchase-confirm-btn"
                          onClick={() => handlePurchaseService(selectedService, serviceTarget)}
                          disabled={purchaseState === 'processing'}
                        >
                          {purchaseState === 'processing' ? (
                            <><FaSpinner className="spin" /> Processing...</>
                          ) : (
                            <><FaBolt /> Pay {selectedService.price.toLocaleString()} KLV</>
                          )}
                        </button>
                      </>
                    )}
                  </div>

                  {purchaseState !== 'success' && (
                    <p className="purchase-disclaimer">
                      <FaInfoCircle />
                      {selectedService.processing === 'automatic'
                        ? ' Automatic services activate within 1 hour of payment.'
                        : ' Manual review services are processed within 24-48 hours after material submission.'
                      }
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorPage;
